import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { InputProps } from "antd";

import { colors } from "@constants/colors";
import { device } from "@constants/styles";

import InputIcon from "./InputIcon";

interface IInputSearchSuggest {
  value: string;
  // Candidate terms, already in the user's language (they come from the data).
  suggestions: string[];
  max?: number;
  onChangeText: (value: string) => void;
}

// InputIcon plus a suggestion list. The list is a plain overlay rather than a
// native <datalist> so it can be styled like the rest of the filter bar and
// behave the same in every browser.
export default function InputSearchSuggest({
  value,
  suggestions,
  max = 6,
  onChangeText,
  ...rest
}: IInputSearchSuggest & Omit<InputProps, "value" | "onChange">) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Substring match on what has been typed; an empty box offers the whole
  // (already short) list, which is how the user finds out what is searchable.
  // Drop the exact term back out — re-picking what is already in the box does
  // nothing but hide the table behind the overlay.
  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    return suggestions
      .filter((s) => s.toLowerCase() !== q)
      .filter((s) => (q ? s.toLowerCase().includes(q) : true))
      .slice(0, max);
  }, [suggestions, value, max]);

  // Close on a click anywhere else: the list overlays the first table rows, so
  // leaving it open after the user has moved on hides data.
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  return (
    <Wrap ref={wrapRef}>
      <InputIcon
        {...rest}
        type="text"
        value={value}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter") setOpen(false);
        }}
        onChange={(e) => {
          onChangeText(e.target.value);
          setOpen(true);
        }}
      />
      {!!value && !rest.disabled && (
        <Clear
          type="button"
          aria-label="clear"
          // mousedown, so the box is emptied before the click can blur-and-close
          onMouseDown={(e) => {
            e.preventDefault();
            onChangeText("");
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </Clear>
      )}
      {open && !rest.disabled && matches.length > 0 && (
        <List>
          {matches.map((s) => (
            <li
              key={s}
              // mousedown, not click: the input blurs first and the row would
              // be gone before a click ever lands.
              onMouseDown={() => {
                onChangeText(s);
                setOpen(false);
              }}
            >
              {s}
            </li>
          ))}
        </List>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const Clear = styled.button`
  position: absolute;
  right: 0.972vw;
  top: 50%;
  transform: translate(0, -50%);
  z-index: 1;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  color: ${colors.grayca4};
  &:hover {
    color: ${colors.blackd33};
  }
  > svg {
    width: 1.111vw;
    height: 1.111vw;
    stroke-width: 2;
    stroke-linecap: round;
  }
  @media ${device.desktopL} {
    right: 14px;
    > svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const List = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  z-index: 10;
  margin: 0;
  padding: 0.278vw 0;
  list-style: none;
  max-height: 15.278vw;
  overflow-y: auto;
  background: ${colors.white};
  border: 1px solid rgba(145, 158, 171, 0.32);
  border-radius: 0.694vw;
  box-shadow: 0px 0.251vw 1.255vw rgba(224, 224, 224, 0.42);
  > li {
    font-family: "SukhumvitSet-Text", sans-serif;
    font-size: 0.972vw;
    color: ${colors.blackd33};
    padding: 0.556vw 1.389vw;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    &:hover {
      background: ${colors.gray5f7};
    }
  }
  @media ${device.desktopL} {
    padding: 4px 0;
    max-height: 220px;
    border-radius: 10px;
    box-shadow: 0px 3.61513px 18.0757px rgba(224, 224, 224, 0.42);
    > li {
      font-size: 14px;
      padding: 8px 20px;
    }
  }
`;
